import { createFileRoute, Link, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { getBackofficeRole, getCounts, type BackofficeRole } from "@/lib/admin-api";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin")({
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/login" });
    }
    return { userId: data.user.id, email: data.user.email ?? "" };
  },
  head: () => ({ meta: [{ title: "Admin, OM Selected" }] }),
  component: AdminLayout,
});

function AdminLayout() {
  const { userId, email } = Route.useRouteContext();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [role, setRole] = useState<BackofficeRole>(null);
  const [pending, setPending] = useState({ applications: 0, community: 0 });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    getBackofficeRole(userId).then((r) => {
      setRole(r);
      setChecking(false);
      if (!r) {
        navigate({ to: "/account/profile" });
      }
    });
  }, [userId, navigate]);

  useEffect(() => {
    getCounts()
      .then((c) => setPending({ applications: c.pendingPhotoApplications + c.pendingVisagistApplications, community: c.pendingCommunity }))
      .catch(() => {});
  }, []);

  if (checking) {
    return <div className="px-10 py-20 text-sm text-muted-foreground">Lädt …</div>;
  }

  if (!role) {
    return <div className="px-10 py-20 text-sm text-muted-foreground">Lädt …</div>;
  }

  const allItems = [
    { to: "/admin", label: "Übersicht", exact: true, curator: true, badge: 0 },
    { to: "/admin/applications", label: "Bewerbungen", curator: true, badge: pending.applications },
    { to: "/admin/photographers", label: "Fotografen", curator: false, badge: 0 },
    { to: "/admin/visagists", label: "Visagist:innen", curator: false, badge: 0 },
    { to: "/admin/briefs", label: "Briefings", curator: false, badge: 0 },
    { to: "/admin/community", label: "Community Selected", curator: true, badge: pending.community },
    { to: "/admin/selected", label: "Selected (Wochen)", curator: true, badge: 0 },
    { to: "/admin/photo-of-the-day", label: "Bild des Tages", curator: false, badge: 0 },
    { to: "/admin/og-preview", label: "Link-Vorschau", curator: false, badge: 0 },
    { to: "/admin/roles", label: "Kurator:innen", curator: false, badge: 0 },
    { to: "/admin/settings", label: "Benachrichtigungen", curator: false, badge: 0 },
  ];
  // Kurator:innen sehen nur die Freigabe-Bereiche; Admins sehen alles.
  const items = role === "admin" ? allItems : allItems.filter((it) => it.curator);

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 grid md:grid-cols-12 gap-10">
      <aside className="md:col-span-3 border-r border-border md:pr-6">
        <div className="eyebrow text-muted-foreground">Backoffice</div>
        <nav className="mt-6 flex flex-col gap-3">
          {items.map((it) => {
            const active = it.exact ? pathname === it.to : pathname.startsWith(it.to);
            return (
              <Link key={it.to} to={it.to} className={`text-sm flex items-center gap-2 ${active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}>
                <span>{it.label}</span>
                {it.badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-medium leading-none">
                    {it.badge}
                  </span>
                )}
              </Link>
            );
          })}
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/login" }); }}
            className="eyebrow text-left text-xs text-muted-foreground hover:text-foreground mt-8"
          >Abmelden ({email})</button>
        </nav>
      </aside>
      <section className="md:col-span-9 min-w-0">
        <Outlet />
      </section>
    </div>
  );
}